import React from 'react';
import { m } from 'motion/react';
import { useLanguage } from '../contexts/LanguageContext';

/** Single team card. The image area stays aspect-square; `zoom`/`focusX`/`focusY`
 *  only control how much of the photo is visible inside that fixed area.
 *  transformOrigin matches objectPosition so the focus point stays put while zooming.
 *  The hover-zoom lives on the wrapper div so it multiplies on top of the base zoom. */
const TeamCard: React.FC<{ member: any; delay: number }> = ({ member, delay }) => {
  const focusX = member.focusX ?? 50;
  const focusY = member.focusY ?? 50;
  const zoom = member.zoom ?? 1;
  return (
    <m.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      viewport={{ once: true }}
      className="glass-card overflow-hidden group w-full lg:w-[250px] max-w-[280px] lg:max-w-none mx-auto lg:mx-0 flex flex-col"
    >
      <div className="aspect-square overflow-hidden flex-shrink-0">
        <div className="w-full h-full transition-transform duration-500 group-hover:scale-105">
          <img
            src={member.image}
            alt={`${member.name} - ${member.role} at PSTax Austria`}
            className="w-full h-full object-cover"
            style={{
              objectPosition: `${focusX}% ${focusY}%`,
              transform: `scale(${zoom})`,
              transformOrigin: `${focusX}% ${focusY}%`,
            }}
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>
      <div className="p-5 flex flex-col flex-grow">
        <h3 className="text-base font-display font-bold mb-0.5 text-pstax-navy">{member.name}</h3>
        <p className="text-pstax-blue text-[11px] font-semibold mb-3 tracking-wider uppercase leading-tight">{member.role}</p>
        <p className="text-pstax-gray text-[13px] leading-relaxed flex-grow">
          {member.desc}
        </p>
      </div>
    </m.div>
  );
};

export const Team: React.FC = () => {
  const { t } = useLanguage();
  const teamT = t.team;

  return (
    <section id="team" className="py-16 bg-white overflow-hidden">
      <div className="container mx-auto px-6">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-display font-bold mb-3">{teamT.title}</h2>
          <p className="text-pstax-gray max-w-2xl mx-auto text-sm md:text-base">
            {teamT.sub}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="space-y-6 lg:space-y-8">
            {/* Row 1: Pascal & Julia */}
            <div className="flex flex-col lg:flex-row justify-center items-stretch gap-6 lg:gap-8">
              {teamT.members.slice(0, 2).map((member: any, i: number) => (
                <TeamCard key={member.name} member={member} delay={i * 0.1} />
              ))}
            </div>

            {/* Row 2: Evelyn, Lisa & Thomas */}
            <div className="flex flex-col lg:flex-row justify-center items-stretch gap-6 lg:gap-8">
              {teamT.members.slice(2).map((member: any, i: number) => (
                <TeamCard key={member.name} member={member} delay={(i + 2) * 0.1} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
